// API Response Types

// RBAC types
export type Role = 'user' | 'reviewer' | 'admin' | 'super_admin';

export const ROLE_HIERARCHY: Record<Role, number> = {
  user: 0,
  reviewer: 1,
  admin: 2,
  super_admin: 3,
};

export const ROLE_LABELS: Record<Role, string> = {
  user: 'User',
  reviewer: 'Reviewer',
  admin: 'Admin',
  super_admin: 'Super Admin',
};

export interface AuthUser {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  org_id?: string;
  org_name?: string;
  agent_tenant_ids: string[] | null; // null = unrestricted (super admin)
}

export interface Org {
  id: string;
  slug: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface Agent {
  id: string;
  org_id: string;
  tenant_id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface ManagedUser {
  id: string;
  email: string;
  display_name: string;
  role: Role;
  org_id?: string;
  is_active: boolean;
  created_at: string;
  last_login_at?: string;
  org_name?: string;
  auth_provider: string;
  pending_approval: boolean;
}

export type Severity = 'critical' | 'high' | 'medium' | 'low';
export type ActionType = 'urgent' | 'monitor';

export interface Tenant {
  id: string;
  name: string;
  description?: string;
}

export interface TenantStatus {
  tenant_id: string;
  tenant_name: string;
  paused: boolean;
  collector_count: number;
  sources: string[];
}

export interface Stats {
  tenant: string;
  total_events: number;
  recent_events: number; // Events collected in last 10 minutes
  pending_count: number;
  total_decisions: number;
  filtered_count: number;
  llm_decided_count: number;
  relevant_count: number;
  avg_score: number;
  avg_llm_score: number;
  by_severity: Record<Severity, number>;
  by_source: Record<string, number>;
  above_threshold_count: number;
  coverage_count: number;
  action_threshold: number;
  relevance_threshold: number;

  // Pre-screen stats
  pre_screen_filtered: number;
  pre_screen_passed: number;
  pre_screen_skipped: number;
  rule_filtered: number;
}

export interface RawEvent {
  id: string;
  tenant_id: string;
  source_id: string;
  external_id: string;
  title: string;
  body: string;
  url: string;
  published_at: string;
  collected_at: string;
  expires_at?: string;
  pipeline_status?: string;
}

export interface FilteredEvent extends RawEvent {
  decision_id?: string;
  reasoning?: string;
  pre_screen_verdict?: string;
  pre_screen_reasoning?: string;
  pre_screen_model?: string;
  confidence?: number;
  decided_at?: string;
}

export interface ScoringBreakdown {
  call_likelihood: number;
  scale_of_impact: number;
  imminence: number;
  geographic_match: number;
  actionability: number;
  hub_multiplier: number;
  imminence_multiplier: number;
  affected_touchpoints: string[];
  estimated_call_impact: string;
}

export interface Decision {
  id: string;
  event_id: string;
  event_title: string;
  event_source: string;
  event_url: string;
  relevance_score: number;
  severity: Severity;
  is_relevant: boolean;
  reasoning: string;
  matched_criteria: string[];
  action_type: ActionType;
  decided_at: string;
  last_alerted_at?: string;
  expires_at?: string;
  pipeline_status?: string;

  // Scoring breakdown — factor-level transparency
  scoring_breakdown?: ScoringBreakdown;

  // LLM confidence
  confidence?: number;
  confidence_reason?: string;

  // Override audit trail
  original_score?: number;
  override_score?: number;
  override_severity?: string;
  override_reason?: string;
  overridden_by?: string;
  overridden_at?: string;

  // Pre-screen audit trail
  pre_screen_verdict?: string;
  pre_screen_reasoning?: string;
  pre_screen_model?: string;
}

export type WorkflowStatus = 'draft' | 'review' | 'approved' | 'published' | 'archived';

export const WORKFLOW_STATUS_ORDER: WorkflowStatus[] = ['draft', 'review', 'approved', 'published', 'archived'];

export const WORKFLOW_TRANSITIONS: Record<WorkflowStatus, WorkflowStatus[]> = {
  draft: ['review', 'archived'],
  review: ['draft', 'approved', 'archived'],
  approved: ['review', 'published', 'archived'],
  published: ['archived'],
  archived: ['draft'],
};

export const WORKFLOW_STATUS_LABELS: Record<WorkflowStatus, string> = {
  draft: 'Draft',
  review: 'In Review',
  approved: 'Approved',
  published: 'Published',
  archived: 'Archived',
};

export const WORKFLOW_STATUS_COLORS: Record<WorkflowStatus, string> = {
  draft: 'bg-gray-500',
  review: 'bg-yellow-500',
  approved: 'bg-blue-500',
  published: 'bg-green-500',
  archived: 'bg-gray-700',
};

export interface CoverageDocument {
  id: string;
  tenant_id: string;
  document_type: string;
  category_id?: string;
  category_name?: string;
  category_icon?: string;
  category_color?: string;
  version: number;
  status: string;
  workflow_status: WorkflowStatus;
  markdown_content: string;
  created_at: string;
  updated_at: string;
  valid_until: string;
  reviewed_at?: string;
  approved_at?: string;
  published_at?: string;
  metadata: {
    severity?: Severity;
    event_id?: string;
    event_title?: string;
    score?: number;
  };
}

export interface CoverageCategory {
  id: string;
  tenant_id: string;
  slug: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  keywords: string[];
  llm_instructions: string;
  template_sections: string[];
  is_default: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ActivityItem {
  type: 'watch' | 'decide' | 'cover';
  message: string;
  timestamp: string;
  score?: number;
  severity?: Severity;
}

// Source polling statistics
export interface SourceStats {
  source_id: string;
  source_type: string;
  tenant_id: string;
  last_polled: string;
  last_poll_status: 'success' | 'error';
  last_error?: string;
  items_found: number;
  new_items: number;
  duplicate_items: number;
  total_polls: number;
}

// Alert from the alerting pipeline
export interface Alert {
  id: string;
  tenant_id: string;
  event_id: string;
  decision_id: string;
  channel_type: string;
  channel_name: string;
  severity: Severity;
  status: 'sent' | 'acknowledged' | 'failed';
  message_preview?: string;
  sent_at: string;
  acknowledged_at?: string;
  acknowledged_by?: string;
  error_message?: string;
  event_title: string;
}

// Score trend data point for time-series charts
export interface TrendPoint {
  bucket: string;
  avg_score: number;
  max_score: number;
  event_count: number;
  relevant_pct: number;
}

// Individual decision point for scatter chart
export interface ScatterPoint {
  decided_at: string;
  score: number;
  severity: Severity;
  relevant: boolean;
  title: string;
  source: string;
}

// Channel configuration
export interface AlertChannel {
  tenant_id: string;
  tenant_name: string;
  type: string;
  name: string;
  enabled: boolean;
  min_severity: string;
}

// UI State Types

export interface FilterState {
  search: string;
  scoreFilter: 'all' | '90' | '75' | '50' | 'below50';
  severityFilter: 'all' | Severity;
  actionFilter: 'all' | ActionType;
  coveredOnly: boolean;
  includeExpired: boolean;
  activeTagFilters: Set<string>;
}

export interface PipelineState {
  watchProcessing: boolean;
  decideProcessing: boolean;
  coverProcessing: boolean;
  throughput: number;
}

export type Tab = 'relevant' | 'all' | 'raw' | 'filtered' | 'live' | 'cover' | 'alerts' | 'analytics';

// ---------------------------------------------------------------
// Agent Config types (mirrors Go config.Config)
// ---------------------------------------------------------------
export interface AgentConfig {
  tenant: {
    id: string;
    name: string;
    description: string;
  };
  watch: {
    poll_interval_secs: number;
    max_article_age_hours: number;
    rss_feeds: RSSFeedConfig[];
    weather_locations: WeatherLocationConfig[];
    status_pages: StatusPageConfig[];
    rest_endpoints: RESTEndpointConfig[];
  };
  decide: {
    llm: { provider: string; model: string; temperature: number; max_tokens: number };
    relevance_threshold: number;
    action_threshold: number;
    scoring: {
      weights: {
        call_likelihood: number;
        scale_of_impact: number;
        imminence: number;
        geographic_match: number;
        actionability: number;
      };
      hub_multipliers: Record<string, number>;
      imminence_decay: {
        current: number;
        within_24h: number;
        within_7_days: number;
        beyond: number;
      };
      customer_journey_touchpoints: string[];
    };
    criticality_levels: {
      level: number;
      name: string;
      min_score: number;
      max_score: number;
      description: string;
      action: string;
    }[];
    business_context: {
      industry: string;
      company_type: string;
      company_name: string;
      primary_concerns: string[];
      regions: { primary: string[]; secondary: string[] };
      operational_focus: {
        hubs: { code: string; name: string; is_home_hub: boolean }[];
        major_routes: string[];
      };
      entities: {
        competitors: string[];
        partners: string[];
        regulators: string[];
        suppliers: string[];
      };
      voice_agent_context: {
        common_call_reasons: string[];
        disruption_questions: string[];
        peak_hours: string;
      };
    };
  };
  cover: {
    alert_threshold: number;
    output: { format: string; directory: string; max_age_hours: number };
    agent_context: {
      agent_name: string;
      agent_role: string;
      brand_voice: string;
      priorities: { critical: string[]; high: string[]; medium: string[]; low: string[] };
      action_framework: Record<string, string>;
    };
    templates: {
      daily_briefing: { enabled: boolean; schedule: string; include_sections: string[] };
      real_time_alerts: { enabled: boolean; trigger_on: Record<string, string>[] };
    };
  };
  alerts: {
    enabled: boolean;
    cooldown_minutes: number;
    channels: AlertChannelConfig[];
  };
}

export interface RSSFeedConfig {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
}

export interface WeatherLocationConfig {
  id: string;
  name: string;
  lat: number;
  lon: number;
  enabled: boolean;
}

export interface StatusPageConfig {
  id: string;
  name: string;
  url: string;
  enabled: boolean;
}

export interface RESTEndpointConfig {
  id: string;
  name: string;
  url: string;
  method: string;
  auth_type: string;
  auth_value: string;
  headers: Record<string, string>;
  body: string;
  items_path: string;
  id_field: string;
  title_field: string;
  body_field: string;
  url_field: string;
  timestamp_field: string;
  enabled: boolean;
}

export interface AlertChannelConfig {
  type: 'slack' | 'teams' | 'email' | 'webhook';
  name: string;
  min_severity: string;
  enabled: boolean;
  webhook_url?: string;
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_password?: string;
  from?: string;
  to?: string[];
  url?: string;
  method?: string;
  headers?: Record<string, string>;
}

// ---------------------------------------------------------------
// ROI Analytics types (mirrors Go ROIAnalytics / DailyROI)
// ---------------------------------------------------------------
export interface ROIAnalytics {
  period_days: number;
  period_from: string;
  period_to: string;

  // Pipeline throughput
  events_collected: number;
  events_decided: number;
  events_relevant: number;
  events_actionable: number;
  coverage_docs: number;
  alerts_sent: number;

  // Pre-screen savings
  pre_screen_filtered: number;
  rule_filtered: number;
  filtered_count: number;
  llm_decided_count: number;
  pre_screen_passed: number;
  pre_screen_evaluated: number;
  llm_cost_saved: number;
  llm_cost_total: number;

  // Call deflection ROI
  roi_configured: boolean;
  currency: string;

  // Modelled ROI — flat configurable spike %
  modelled_calls_deflected: number;
  modelled_cost_saved: number;
  modelled_monthly: number;

  // Actual ROI — per-event LLM-assessed spike %
  actual_calls_deflected: number;
  actual_cost_saved: number;
  actual_monthly: number;

  // Legacy
  est_calls_deflected: number;
  est_time_saved_hours: number;
  est_cost_saved: number;
  est_cost_per_event_cover: number;
  monthly_projection: number;

  // Efficiency metrics
  filter_rate: number;
  relevance_rate: number;
  actionable_rate: number;
  coverage_rate: number;

  // Daily breakdown for charting
  daily_breakdown: DailyROI[];

  // Config
  config: ROIConfig;
}

export interface DailyROI {
  date: string;
  events_collected: number;
  events_decided: number;
  events_relevant: number;
  events_filtered: number;
  events_actionable: number;
  coverage_docs: number;
  alerts_sent: number;

  // Modelled: flat config spike % applied uniformly
  modelled_spike_cost: number;    // Cost of the spike without Sentinel
  modelled_deflect_cost: number;  // Remaining cost with Sentinel

  // Actual: per-event LLM-assessed spike %
  actual_spike_cost: number;      // Cost of actual assessed spikes
  actual_deflect_cost: number;    // Remaining cost with Sentinel

  // Legacy
  est_calls_deflected: number;
  est_cost_saved: number;
}

export interface ROIConfig {
  enabled: boolean;
  daily_call_volume: number;
  cost_per_call: number;
  call_spike_pct: number;
  deflection_rate: number;
  cost_per_llm_call: number;
  cost_per_pre_screen: number;
  currency: string;
}

// Agent API Keys
export interface AgentAPIKey {
  id: string;
  tenant_id: string;
  key_prefix: string;
  name: string;
  scopes: string[];
  rate_limit: number;
  is_active: boolean;
  last_used_at?: string;
  expires_at?: string;
  created_at: string;
}

export interface CreateAgentAPIKeyResponse extends AgentAPIKey {
  key: string; // raw key, only returned on creation
}

// Constants
export const SEVERITY_ORDER: Severity[] = ['critical', 'high', 'medium', 'low'];

export const NO_IFRAME_SOURCES = [
  'bbc.co.uk', 'bbc.com',
  'reuters.com',
  'nytimes.com',
  'theguardian.com',
  'ft.com',
  'wsj.com',
  'bloomberg.com',
  'cnn.com',
  'washingtonpost.com',
  'forbes.com'
];

// Prompt overrides: maps prompt name → variable name → override value
export type PromptOverridesMap = Record<string, Record<string, string>>;

/** Describes an LLM instruction field that can be overridden per-agent. */
export interface LLMInstructionField {
  /** Variable key in the decision-system prompt template. */
  key: string;
  /** Friendly display label. */
  label: string;
  /** Short description shown below the label. */
  description: string;
  /** Detailed help shown in the tooltip. */
  help: string;
  /** Placeholder text for the textarea. */
  placeholder: string;
  /** Preview of the auto-generated default text. */
  defaultPreview: string;
}

/**
 * The five decision-system instruction variables that can be customised per-agent.
 * All other prompt variables are either auto-derived from agent config (company name,
 * industry, concerns, hubs, etc.) or injected from runtime event data.
 */
export const LLM_INSTRUCTION_FIELDS: LLMInstructionField[] = [
  {
    key: 'critical_reminders',
    label: 'Scoring Reminders',
    description: 'Extra rules the LLM should follow when scoring events for this agent.',
    help: 'These reminders are appended to the scoring prompt as numbered rules. Use them to highlight domain-specific edge cases, adjust scoring bias, or flag specific event types. For example: "Always flag volcanic ash events as critical" or "Competitor pricing changes rarely drive customer calls."',
    placeholder: 'e.g. Always flag volcanic ash cloud events as critical for this airline...\ne.g. Discount/sale events from competitors should score low unless they indicate market disruption...',
    defaultPreview: `Auto-generated based on industry. Typical defaults include:\n\n1. Check the dates — past events aren't actionable\n2. False negatives are worse than false positives — score higher if unsure\n3. Focus on uncertainty — "what does this mean for me?" drives calls\n4. Backend issues don't drive calls unless visible to customers\n5. Competitor news rarely drives calls\n6. Historical/past events → is_past_event: true with low imminence\n7. Think like a worried customer\n\nIndustry-specific additions (e.g. aviation: "Weather and security events at airports are highest priority")`,
  },
  {
    key: 'call_likelihood_examples',
    label: 'Call Likelihood Examples',
    description: 'Examples that teach the LLM what scores high or low for call likelihood.',
    help: 'Provide concrete, industry-specific scenarios showing how to score call likelihood from 0.0 to 1.0. The LLM uses these as reference points when evaluating events. Good examples include both high-scoring events (service outages, disruptions) and low-scoring ones (internal changes, minor updates).',
    placeholder: 'e.g. Score 0.8-1.0: Payment gateway outage — customers can\'t complete orders and will call asking "why can\'t I pay?"\ne.g. Score 0.0-0.1: Internal warehouse reorganisation with no customer-facing impact...',
    defaultPreview: `Auto-generated based on industry. Example (retail):\n\n**Score 0.8-1.0:** Event creates immediate uncertainty about the customer's order — payment outages, website down, delivery strikes, warehouse closures.\nKey question: "Where is my order?"\n\n**Score 0.0-0.1:** Internal operational changes, industry news with no direct customer impact.`,
  },
  {
    key: 'scale_of_impact_examples',
    label: 'Scale of Impact Examples',
    description: 'Examples showing how to assess the breadth of customer impact.',
    help: 'Define what "high impact" vs "low impact" means for this agent\'s business. The LLM uses these to gauge whether an event affects thousands of customers or just a handful. Include quantitative guidance where possible.',
    placeholder: 'e.g. Score 0.8-1.0: System-wide outage affecting all customers across all regions\ne.g. Score 0.2-0.4: Localised issue affecting a single warehouse or delivery route...',
    defaultPreview: `Auto-generated based on industry. Example (retail):\n\n**Score 0.8-1.0:** Thousands affected — website outage, payment system down, major courier strike\n**Score 0.5-0.7:** Hundreds affected — regional warehouse issue, single carrier delay\n**Score 0.2-0.4:** Dozens affected — minor localised disruption\n**Score 0.0-0.1:** Minimal customer impact`,
  },
  {
    key: 'geographic_context',
    label: 'Geographic Scoring Guidance',
    description: 'Instructions for how geography should influence event scoring.',
    help: 'Provide guidance on which regions, locations, or operational hubs matter most. The LLM already knows the agent\'s configured hubs and regions — use this to add nuance like seasonal patterns, hub priorities, or region-specific escalation rules.',
    placeholder: 'e.g. Events at Heathrow (LHR) should score higher than other hubs during summer due to peak traffic\ne.g. EU regulatory changes score high as they affect our primary market...',
    defaultPreview: `Auto-generated based on industry and configured hubs.\n\nTypically includes:\n- Guiding question (e.g. "Does this affect our operational hubs and routes?")\n- List of primary hub codes from agent config\n- 4-tier geographic scoring (0.8-1.0 for primary hubs, down to 0.0-0.1 for unrelated regions)`,
  },
  {
    key: 'actionability_examples',
    label: 'Actionability Examples',
    description: 'Examples showing what makes an event actionable for customer-facing agents.',
    help: 'Define what "actionable" means in the context of this agent — i.e., can a customer-facing agent give useful, concrete advice based on this event? High actionability means clear steps (rebook, use alternative, check status page). Low means vague or no practical guidance.',
    placeholder: 'e.g. Score 0.8-1.0: Flight cancelled — agent can offer rebooking, hotel voucher, or refund\ne.g. Score 0.0-0.1: Regulatory policy discussion — no concrete action for customer-facing staff...',
    defaultPreview: `Auto-generated based on industry. Example (retail):\n\n**Score 0.8-1.0:** Clear customer action available — track order, use alternative payment, arrange redelivery\n**Score 0.5-0.7:** Can acknowledge issue and promise updates\n**Score 0.2-0.4:** Can only say "we're monitoring the situation"\n**Score 0.0-0.1:** No useful guidance — purely informational`,
  },
];
